import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { listTools, updateTool } from '../engine/ToolRegistry'
import type { Tool } from '../engine/types'
import {
  Search, Filter, Globe, Calculator, Map, Database, Code, Wrench,
  Loader, AlertTriangle, RefreshCw, CheckCircle, XCircle, Info,
  X, Terminal, Play, BookOpen, ChevronRight
} from 'lucide-react'
import { useLang } from '../i18n/LangContext'
import type { Lang } from '../i18n/translations'

const categoryMeta: Record<string, { icon: typeof Globe; label: string; color: string }> = {
  browser: { icon: Globe, label: 'Browser', color: '#6c8cff' },
  search: { icon: Search, label: 'Search', color: '#4ade80' },
  map: { icon: Map, label: 'Map', color: '#fbbf24' },
  calculation: { icon: Calculator, label: 'Calculation', color: '#60a5fa' },
  data: { icon: Database, label: 'Data', color: '#c084fc' },
  code: { icon: Code, label: 'Code', color: '#f87171' },
  other: { icon: Wrench, label: 'Other', color: '#8b8fa8' },
}

// Parsed function schema shape
interface FunctionSchema {
  name: string
  description: string
  parameters: {
    type: string
    properties: Record<string, {
      type?: string
      description?: string
      enum?: string[]
    }>
    required?: string[]
  }
}

// Usage guide text per category, per language
const usageGuideText: Record<string, Record<Lang, string>> = {
  browser: {
    zh: '使用此工具浏览网页、读取内容，并从 URL 中提取信息。',
    en: 'Use this tool to browse web pages, read content, and extract information from URLs.',
  },
  search: {
    zh: '使用此工具在互联网上搜索信息、价格、评论和时事。',
    en: 'Use this tool to search the internet for information, prices, reviews, and current events.',
  },
  map: {
    zh: '使用此工具查询地理位置、规划路线、计算距离并估算出行时间。',
    en: 'Use this tool to query locations, plan routes, calculate distances, and estimate travel times.',
  },
  calculation: {
    zh: '使用此工具进行数学计算、预算分析和成本对比。',
    en: 'Use this tool for mathematical calculations, budget analysis, and cost comparisons.',
  },
  data: {
    zh: '使用此工具在电子表格中整理数据、对比选项，并进行结构化分析。',
    en: 'Use this tool to organize data in spreadsheets, compare options, and perform structured analysis.',
  },
  code: {
    zh: '使用此工具执行代码片段，用于数据处理或结果验证。',
    en: 'Use this tool to execute code snippets for data processing or verification.',
  },
  other: {
    zh: '根据任务需要使用此工具。',
    en: 'Use this tool as needed for the task.',
  },
}

// Mock response per category
function generateMockResponse(category: string): Record<string, unknown> {
  switch (category) {
    case 'browser':
      return {
        status: 'success',
        data: {
          title: 'Example Page Title',
          url: 'https://example.com',
          content: 'Extracted page content snippet...',
        },
      }
    case 'search':
      return {
        status: 'success',
        data: {
          results: [
            { title: 'Result 1', url: 'https://example.com/1', snippet: '...' },
            { title: 'Result 2', url: 'https://example.com/2', snippet: '...' },
          ],
          total: 2,
        },
      }
    case 'map':
      return {
        status: 'success',
        data: {
          distance: '12.5 km',
          duration: '25 min',
          mode: 'driving',
          route: ['Start', 'Waypoint', 'Destination'],
        },
      }
    case 'calculation':
      return {
        status: 'success',
        data: {
          expression: '100 * 2 + 50',
          result: 250,
        },
      }
    case 'data':
      return {
        status: 'success',
        data: {
          rows: [
            { id: 1, name: 'Option A', value: 100 },
            { id: 2, name: 'Option B', value: 120 },
          ],
          columns: ['id', 'name', 'value'],
        },
      }
    case 'code':
      return {
        status: 'success',
        data: {
          stdout: 'program output',
          exit_code: 0,
        },
      }
    default:
      return {
        status: 'success',
        data: {},
      }
  }
}

// Generate mock args from the schema parameters
function generateMockArgs(schema: FunctionSchema | null): Record<string, unknown> {
  if (!schema?.parameters?.properties) return {}
  const args: Record<string, unknown> = {}
  for (const [key, prop] of Object.entries(schema.parameters.properties)) {
    const propType = prop.type
    if (key === 'url') {
      args[key] = 'https://example.com'
    } else if (key === 'query') {
      args[key] = 'search term'
    } else if (key === 'origin') {
      args[key] = 'City A'
    } else if (key === 'destination') {
      args[key] = 'City B'
    } else if (key === 'expression') {
      args[key] = '100 * 2 + 50'
    } else if (key === 'action') {
      args[key] = prop.enum?.[0] ?? 'create'
    } else if (key === 'mode') {
      args[key] = prop.enum?.[0] ?? 'driving'
    } else if (key === 'data') {
      args[key] = [{ id: 1, name: 'item' }]
    } else if (propType === 'string') {
      args[key] = 'value'
    } else if (propType === 'number' || propType === 'integer') {
      args[key] = 0
    } else if (propType === 'boolean') {
      args[key] = false
    } else if (propType === 'array') {
      args[key] = []
    } else if (propType === 'object') {
      args[key] = {}
    } else {
      args[key] = null
    }
  }
  return args
}

// Safely parse the function_schema JSON string
function parseFunctionSchema(raw: string): FunctionSchema | null {
  try {
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.name === 'string' &&
      typeof parsed.description === 'string' &&
      typeof parsed.parameters === 'object'
    ) {
      return parsed as FunctionSchema
    }
    return null
  } catch {
    return null
  }
}

export default function ToolRegistryPage() {
  const { t, lang } = useLang()
  const [tools, setTools] = useState<Tool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null)

  const fetchTools = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listTools()
      setTools(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tools')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTools()
  }, [])

  // Close modal on Escape key
  useEffect(() => {
    if (!selectedTool) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedTool(null)
    }
    window.addEventListener('keydown', handleKeyDown)
    // Lock body scroll while modal open
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [selectedTool])

  const handleToggle = async (tool: Tool) => {
    setTogglingId(tool.id)
    const newStatus = tool.status === 'active' ? 'inactive' : 'active'
    try {
      await updateTool(tool.id, { status: newStatus })
      setTools(prev =>
        prev.map(t => (t.id === tool.id ? { ...t, status: newStatus } : t))
      )
      // Keep the selected tool in sync if it's the one being toggled
      setSelectedTool(prev => (prev && prev.id === tool.id ? { ...prev, status: newStatus } : prev))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tool')
    } finally {
      setTogglingId(null)
    }
  }

  const categories = ['all', ...new Set(tools.map(t => t.category))]

  const filtered = tools.filter(t => {
    const matchesSearch =
      !searchQuery ||
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.name_cn.includes(searchQuery) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = filterCategory === 'all' || t.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const getCategoryIcon = (category: string) => {
    const meta = categoryMeta[category] ?? categoryMeta.other
    const Icon = meta.icon
    return <Icon size={20} style={{ color: meta.color }} />
  }

  const getCategoryLabel = (category: string) => {
    return categoryMeta[category]?.label ?? category
  }

  const getCategoryColor = (category: string) => {
    return categoryMeta[category]?.color ?? '#8b8fa8'
  }

  const getUsageGuide = useCallback(
    (category: string) => {
      return (usageGuideText[category] ?? usageGuideText.other)[lang]
    },
    [lang]
  )

  // ---- Loading State ----
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // ---- Error State ----
  if (error && tools.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248,113,113,0.12)' }}>
            <AlertTriangle size={28} style={{ color: 'var(--color-danger)' }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('common.error')}</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          </div>
          <button
            onClick={fetchTools}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            <RefreshCw size={15} /> {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  // ---- Main Content ----
  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('tool.title')}</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {t('tool.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {tools.length} {t('tool.registered')}
          </span>
        </div>
      </div>

      {/* Error Banner */}
      {error && tools.length > 0 && (
        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg mb-6 text-sm"
          style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: 'var(--color-danger)', border: '1px solid rgba(248,113,113,0.25)' }}
        >
          <AlertTriangle size={15} />
          {error}
          <button onClick={fetchTools} className="ml-auto underline text-xs">{t('common.retry')}</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            placeholder={t('tool.searchPlaceholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg text-sm outline-none transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
            }}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={15} style={{ color: 'var(--color-text-muted)' }} />
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize"
              style={{
                backgroundColor: filterCategory === cat ? 'var(--color-accent-glow)' : 'var(--color-bg-tertiary)',
                color: filterCategory === cat ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                border: `1px solid ${filterCategory === cat ? 'var(--color-accent)' : 'var(--color-border)'}`,
              }}
            >
              {cat === 'all' ? 'All' : getCategoryLabel(cat)}
            </button>
          ))}
        </div>
      </div>

      {/* Tool Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Info size={40} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
          <p className="mt-4" style={{ color: 'var(--color-text-secondary)' }}>No tools match your criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(tool => (
            <div
              key={tool.id}
              className="rounded-xl p-5 transition-colors cursor-pointer group"
              style={{
                backgroundColor: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
              }}
              onClick={() => setSelectedTool(tool)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${getCategoryColor(tool.category)}15` }}
                  >
                    {getCategoryIcon(tool.category)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {tool.name}
                    </h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {tool.name_cn}
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggle(tool)
                  }}
                  disabled={togglingId === tool.id}
                  className="relative inline-flex h-6 w-10 items-center rounded-full transition-colors shrink-0"
                  style={{
                    backgroundColor: tool.status === 'active' ? 'var(--color-success)' : 'var(--color-border)',
                    opacity: togglingId === tool.id ? 0.6 : 1,
                  }}
                >
                  {togglingId === tool.id ? (
                    <span className="absolute left-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                      <Loader size={10} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                    </span>
                  ) : (
                    <span
                      className="absolute flex h-4 w-4 items-center justify-center rounded-full bg-white transition-transform"
                      style={{
                        left: tool.status === 'active' ? 'calc(100% - 18px)' : '2px',
                      }}
                    >
                      {tool.status === 'active' ? (
                        <CheckCircle size={10} style={{ color: 'var(--color-success)' }} />
                      ) : (
                        <XCircle size={10} style={{ color: 'var(--color-text-muted)' }} />
                      )}
                    </span>
                  )}
                </button>
              </div>

              <p className="text-xs mb-4 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                {tool.description}
              </p>

              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="px-2 py-0.5 rounded text-[11px] font-medium capitalize"
                  style={{
                    backgroundColor: `${getCategoryColor(tool.category)}18`,
                    color: getCategoryColor(tool.category),
                  }}
                >
                  {getCategoryLabel(tool.category)}
                </span>
                <span
                  className="px-2 py-0.5 rounded text-[11px] font-medium"
                  style={{
                    backgroundColor: tool.requires_sandbox ? 'rgba(251,191,36,0.12)' : 'rgba(74,222,128,0.12)',
                    color: tool.requires_sandbox ? 'var(--color-warning)' : 'var(--color-success)',
                  }}
                >
                  {tool.requires_sandbox ? t('tool.sandbox') : t('tool.noSandbox')}
                </span>
                <span
                  className="px-2 py-0.5 rounded text-[11px] font-medium"
                  style={{
                    backgroundColor: tool.status === 'active' ? 'rgba(74,222,128,0.12)' : 'rgba(139,143,168,0.12)',
                    color: tool.status === 'active' ? 'var(--color-success)' : 'var(--color-text-muted)',
                  }}
                >
                  {tool.status === 'active' ? t('tool.active') : t('tool.inactive')}
                </span>
              </div>

              {/* View Details indicator */}
              <div
                className="mt-4 pt-3 flex items-center gap-1 text-xs font-medium transition-colors"
                style={{
                  color: 'var(--color-text-muted)',
                  borderTop: '1px solid var(--color-border)',
                }}
              >
                <span>{t('tool.viewDetails')}</span>
                <ChevronRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                  style={{ color: 'var(--color-accent)' }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Detail Modal ---- */}
      {selectedTool && (
        <ToolDetailModal
          tool={selectedTool}
          lang={lang}
          onClose={() => setSelectedTool(null)}
          onToggle={handleToggle}
          togglingId={togglingId}
          getCategoryIcon={getCategoryIcon}
          getCategoryLabel={getCategoryLabel}
          getCategoryColor={getCategoryColor}
          getUsageGuide={getUsageGuide}
        />
      )}
    </div>
  )
}

// ============ Tool Detail Modal ============

interface ToolDetailModalProps {
  tool: Tool
  lang: Lang
  onClose: () => void
  onToggle: (tool: Tool) => void
  togglingId: string | null
  getCategoryIcon: (category: string) => ReactNode
  getCategoryLabel: (category: string) => string
  getCategoryColor: (category: string) => string
  getUsageGuide: (category: string) => string
}

function ToolDetailModal({
  tool,
  lang,
  onClose,
  onToggle,
  togglingId,
  getCategoryIcon,
  getCategoryLabel,
  getCategoryColor,
  getUsageGuide,
}: ToolDetailModalProps) {
  const { t } = useLang()
  const schema = parseFunctionSchema(tool.function_schema)
  const mockArgs = generateMockArgs(schema)
  const mockResponse = generateMockResponse(tool.category)

  const agentCallExample = {
    tool: schema?.name ?? tool.name.toLowerCase(),
    args: mockArgs,
  }

  const categoryColor = getCategoryColor(tool.category)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl"
        style={{
          backgroundColor: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          className="sticky top-0 z-10 flex items-start justify-between gap-4 p-6"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div className="flex items-center gap-4 min-w-0">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${categoryColor}15` }}
            >
              {getCategoryIcon(tool.category)}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>
                {tool.name}
              </h2>
              <p className="text-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>
                {tool.name_cn}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('tool.close')}
            className="p-2 rounded-lg transition-colors shrink-0"
            style={{
              color: 'var(--color-text-muted)',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'
              e.currentTarget.style.color = 'var(--color-text-primary)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = 'var(--color-text-muted)'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Badges row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="px-2.5 py-1 rounded-md text-xs font-medium capitalize"
              style={{
                backgroundColor: `${categoryColor}18`,
                color: categoryColor,
              }}
            >
              {getCategoryLabel(tool.category)}
            </span>
            <span
              className="px-2.5 py-1 rounded-md text-xs font-medium"
              style={{
                backgroundColor: tool.status === 'active' ? 'rgba(74,222,128,0.12)' : 'rgba(139,143,168,0.12)',
                color: tool.status === 'active' ? 'var(--color-success)' : 'var(--color-text-muted)',
              }}
            >
              {tool.status === 'active' ? t('tool.active') : t('tool.inactive')}
            </span>
            <span
              className="px-2.5 py-1 rounded-md text-xs font-medium"
              style={{
                backgroundColor: tool.requires_sandbox ? 'rgba(251,191,36,0.12)' : 'rgba(74,222,128,0.12)',
                color: tool.requires_sandbox ? 'var(--color-warning)' : 'var(--color-success)',
              }}
            >
              {tool.requires_sandbox ? t('tool.sandbox') : t('tool.noSandbox')}
            </span>
          </div>

          {/* Description */}
          <div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
              {tool.description}
            </p>
          </div>

          {/* Toggle control */}
          <div className="flex items-center justify-between gap-3 py-3 px-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {t('common.status')}
            </span>
            <button
              onClick={() => onToggle(tool)}
              disabled={togglingId === tool.id}
              className="relative inline-flex h-6 w-10 items-center rounded-full transition-colors shrink-0"
              style={{
                backgroundColor: tool.status === 'active' ? 'var(--color-success)' : 'var(--color-border)',
                opacity: togglingId === tool.id ? 0.6 : 1,
              }}
            >
              {togglingId === tool.id ? (
                <span className="absolute left-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                  <Loader size={10} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                </span>
              ) : (
                <span
                  className="absolute flex h-4 w-4 items-center justify-center rounded-full bg-white transition-transform"
                  style={{
                    left: tool.status === 'active' ? 'calc(100% - 18px)' : '2px',
                  }}
                >
                  {tool.status === 'active' ? (
                    <CheckCircle size={10} style={{ color: 'var(--color-success)' }} />
                  ) : (
                    <XCircle size={10} style={{ color: 'var(--color-text-muted)' }} />
                  )}
                </span>
              )}
            </button>
          </div>

          {/* Function Schema Section */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Terminal size={16} style={{ color: 'var(--color-accent)' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {t('tool.functionSchema')}
              </h3>
            </div>
            {schema ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                    <p className="text-[11px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--color-text-muted)' }}>
                      {lang === 'zh' ? '名称' : 'Name'}
                    </p>
                    <p className="text-sm font-mono" style={{ color: 'var(--color-text-primary)' }}>
                      {schema.name}
                    </p>
                  </div>
                  <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                    <p className="text-[11px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--color-text-muted)' }}>
                      {lang === 'zh' ? '描述' : 'Description'}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {schema.description}
                    </p>
                  </div>
                </div>
                <pre
                  className="overflow-x-auto rounded-lg p-4 text-xs leading-relaxed"
                  style={{
                    backgroundColor: '#0d1117',
                    color: '#c9d1d9',
                    border: '1px solid var(--color-border)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  }}
                >
                  {JSON.stringify(schema, null, 2)}
                </pre>
              </div>
            ) : (
              <pre
                className="overflow-x-auto rounded-lg p-4 text-xs leading-relaxed"
                style={{
                  backgroundColor: '#0d1117',
                  color: '#f87171',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                }}
              >
                {lang === 'zh' ? '// 无法解析函数Schema' : '// Failed to parse function schema'}
                {'\n'}
                {tool.function_schema}
              </pre>
            )}
          </section>

          {/* Usage Example Section */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Play size={16} style={{ color: 'var(--color-accent)' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {t('tool.usageExample')}
              </h3>
            </div>
            <pre
              className="overflow-x-auto rounded-lg p-4 text-xs leading-relaxed"
              style={{
                backgroundColor: '#0d1117',
                color: '#c9d1d9',
                border: '1px solid var(--color-border)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              }}
            >
{`// ${lang === 'zh' ? t('tool.agentCall') : 'Agent calls this tool like:'}
${JSON.stringify(agentCallExample, null, 2)}

// ${t('tool.expectedResponse')}:
${JSON.stringify(mockResponse, null, 2)}`}
            </pre>
          </section>

          {/* Usage Guide Section */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen size={16} style={{ color: 'var(--color-accent)' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {t('tool.usageGuide')}
              </h3>
            </div>
            <div
              className="px-4 py-3 rounded-lg text-sm leading-relaxed"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-secondary)',
                borderLeft: `3px solid ${categoryColor}`,
              }}
            >
              {getUsageGuide(tool.category)}
            </div>
          </section>
        </div>

        {/* Modal Footer */}
        <div
          className="sticky bottom-0 flex items-center justify-end gap-3 p-4"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
            }}
          >
            {t('tool.close')}
          </button>
        </div>
      </div>
    </div>
  )
}
